import { useEffect, useState } from 'react';

// Gece (the site's dark look, the default), Gündüz, or whatever the device is set to. The choice is
// kept per device: the packing desk's PC in a bright hall and the pickers' phones can differ.
// public/theme.js applies it before the first paint; this keeps it in step while the app runs.

export type ThemeChoice = 'dark' | 'light' | 'system';

const KEY = 'wms.theme';
const THEME_COLOR = { dark: '#020617', light: '#ffffff' } as const;

function stored(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'system' ? v : 'dark';
  } catch {
    return 'dark';
  }
}

const deviceIsLight = () => window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;

function apply(choice: ThemeChoice): void {
  const mode = choice === 'light' || (choice === 'system' && deviceIsLight()) ? 'light' : 'dark';
  document.documentElement.dataset.theme = mode;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[mode]);
}

/** The device's theme choice and a setter that applies and keeps it. */
export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(stored);

  useEffect(() => {
    apply(choice);
    if (choice !== 'system') return;
    // following the device: the phone's own day/night switch takes effect at once
    const media = window.matchMedia?.('(prefers-color-scheme: light)');
    const follow = () => apply('system');
    media?.addEventListener('change', follow);
    return () => media?.removeEventListener('change', follow);
  }, [choice]);

  const set = (next: ThemeChoice) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // storage blocked: it still applies until the page is closed
    }
    setChoice(next);
  };
  return [choice, set];
}

/** Applies the stored choice when the app starts (the page may have been open since another tab changed it). */
export function applyStoredTheme(): void {
  apply(stored());
}
